import { useEffect, useRef } from 'react';
import { KeyRound } from 'lucide-react';
import type { TenantBrand } from '@tendedloop/shared';
import { bandColors, contrastRatio } from '@tendedloop/shared/brand-colors';
import type { Room } from '@/lib/api';
import { onHighlight } from '@/lib/brand';

/** Shown once per handover, and only in the first month after it. */
const WINDOW_DAYS = 30;

// Keyed on the handover itself, so the flag on a shared phone names no apartment.
const seenKey = (room: Room) => `welcome_seen_${room.keyHandedAt ?? ''}`;

// Dismissed in this visit (also when storage is full and the flag cannot be kept).
const dismissed = new Set<string>();

/** Should the move-in welcome open now? */
export function shouldWelcome(brand: TenantBrand | null, room: Room | null): boolean {
  if (!brand || !room?.keyHandedAt || dismissed.has(seenKey(room))) return false;
  const age = Date.now() - new Date(room.keyHandedAt).getTime();
  if (!(age >= 0 && age < WINDOW_DAYS * 86_400_000)) return false;
  try {
    return localStorage.getItem(seenKey(room)) !== '1';
  } catch {
    return false; // no storage: never risk showing it on every visit
  }
}

/**
 * The move-in moment: the key is in the resident's hand. The one screen that is
 * all in the brand's band colour (black for most brands), because it is a
 * celebration and not a working screen.
 */
export function WelcomeHome({ brand, room, onClose }: { brand: TenantBrand; room: Room; onClose: () => void }) {
  const button = useRef<HTMLButtonElement>(null);
  const band = bandColors(brand.logoBackground);
  // The brand's "done" colour on the band, when it reads there (teal on black does, 9.7:1).
  const accent = brand.done && contrastRatio(brand.done, band.bg) >= 4.5 ? brand.done : band.fg;

  const close = () => {
    dismissed.add(seenKey(room));
    try {
      localStorage.setItem(seenKey(room), '1');
    } catch {
      /* storage full or unavailable: the in-memory flag keeps it closed for this visit */
    }
    onClose();
  };

  useEffect(() => {
    button.current?.focus();
    try {
      navigator.vibrate?.([30, 50, 30]);
    } catch {
      /* no haptics on this device */
    }
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="welcome-title"
      data-brand-level="identity"
      className="fixed inset-0 z-50 flex flex-col overflow-y-auto"
      style={{ backgroundColor: band.bg, color: band.fg }}
    >
      {brand.splashUrl && (
        <img src={brand.splashUrl} alt="" aria-hidden className="pointer-events-none mt-10 h-24 w-full object-cover" />
      )}
      <div className="mx-auto flex w-full max-w-md flex-1 flex-col items-center px-6 pt-10 text-center">
        <div
          className="flex h-24 w-24 animate-[pop_.45s_ease-out] items-center justify-center rounded-3xl bg-highlight"
          style={{ color: onHighlight(brand) }}
        >
          <KeyRound className="h-12 w-12" strokeWidth={1.75} aria-hidden />
        </div>
        <h1 id="welcome-title" className="mt-6 text-[32px] font-bold leading-tight">
          ברוכים הבאים הביתה
        </h1>
        <p className="mt-2 text-[19px] font-semibold" style={{ color: accent }}>
          המפתח לדירה {room.apartment} אצלך
        </p>
        {!!brand.features?.length && (
          <ul className="mt-9 flex w-full justify-around gap-2">
            {brand.features.map((f) => (
              <li key={f.label} className="flex flex-col items-center gap-2 text-[16px]">
                {f.iconUrl && <img src={f.iconUrl} alt="" aria-hidden className="h-14 w-14" />}
                {f.label}
              </li>
            ))}
          </ul>
        )}
        <div className="mt-auto w-full pb-10 pt-10">
          <button
            ref={button}
            type="button"
            onClick={close}
            className="min-h-[52px] w-full rounded-2xl text-[17px] font-bold"
            style={{ backgroundColor: band.fg, color: band.bg }}
          >
            לדף הבית
          </button>
        </div>
      </div>
      <style>{`@keyframes pop{0%{transform:scale(.6);opacity:0}60%{transform:scale(1.08)}100%{transform:scale(1);opacity:1}}`}</style>
    </div>
  );
}
